/**
 * Hotel GST Details Thunks
 *
 * Redux thunks for GST operations (add/edit/remove).
 * Backend is the single source of truth - frontend NEVER derives state locally.
 *
 * Trigger conditions (no explicit "Save" button):
 * - ADD: When GST checkbox is checked, all 3 fields filled & valid, and blurs out of ANY field
 *        (only if not already saved to backend)
 * - EDIT: When GST is already saved, any field changes and blurs out
 *         (only if there are actual changes compared to last saved state)
 * - REMOVE: When GST checkbox is unchecked (only if GST was previously saved)
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { GstDetailActionType, GstDetailRequest } from '@contracts';
import { callGstDetailsApi, GstDetailsError } from '../../api';
import type { RootState, AppDispatch } from '@/store/store';
import { logger } from '@/shared/utils/logger';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';

// ============================================
// Types
// ============================================

interface GstOperationParams {
  /** Transaction key from review response */
  transactionKey: string;
  /** GST details for the operation (null for remove) */
  gstDetails: GstDetailRequest | null;
}

interface GstOperationResult {
  /** Updated GST details (null if removed) */
  gstDetails: GstDetailRequest | null;
  /** Whether GST is currently applied - from API response (resources[0].data.isGstApplied) */
  isGstApplied: boolean;
}

interface RemoveGstParams {
  /** Transaction key from review response */
  transactionKey: string;
}

// ============================================
// Generic Thunk Creator
// ============================================

function createGstThunk(name: string, actionType: GstDetailActionType) {
  return createAsyncThunk<
    GstOperationResult,
    GstOperationParams,
    { state: RootState; dispatch: AppDispatch; rejectValue: string }
  >(
    `hotels/gst/${name}`,
    async (params, { rejectWithValue }) => {
      try {
        const sessionId = getOrCreateSessionId();
        const userId = getUUID();

        logger.info(`[GstThunk:${name}] Starting operation`, {
          hasGstDetails: params.gstDetails !== null,
          gstNumber: params.gstDetails?.gstNumber,
        });

        const response = await callGstDetailsApi({
          sessionId,
          userId,
          transactionKey: params.transactionKey,
          actionType,
          gstDetails: params.gstDetails,
        });

        if (!response.success && response.error) {
          logger.warn(`[GstThunk:${name}] API error:`, response.error);
          return rejectWithValue(response.error.message);
        }

        logger.info(`[GstThunk:${name}] Success`, {
          hasGstDetails: response.gstDetails !== null,
          isGstApplied: response.isGstApplied,
        });

        // Return the GST details and isGstApplied from backend
        // Frontend MUST override its state with this - no local computation
        return { gstDetails: response.gstDetails, isGstApplied: response.isGstApplied };
      } catch (error) {
        logger.error(`[GstThunk:${name}] Failed:`, error);
        if (error instanceof GstDetailsError) {
          return rejectWithValue(error.message);
        }
        return rejectWithValue(`Failed to ${name} GST details`);
      }
    },
    {
      // Prevent duplicate API calls while loading
      condition: (_, { getState }) => {
        const state = getState() as RootState;
        return !state.hotels.bookingReview.isGstOperationLoading;
      },
    },
  );
}

// ============================================
// Thunks
// ============================================

/**
 * Add GST details
 * Called when:
 * - GST checkbox is checked
 * - All 3 fields are filled and valid
 * - User blurs out of ANY field
 * - GST not already saved to backend
 */
export const addGstThunk = createGstThunk('add', 'hotel_gst_detail_add');

/**
 * Edit existing GST details
 * Called when:
 * - GST is already saved to backend
 * - Any field changes
 * - User blurs out of the changed field
 * - Only if there are actual changes compared to last saved state
 */
export const editGstThunk = createGstThunk('edit', 'hotel_gst_detail_edit');

/**
 * Remove GST details
 * Called when GST checkbox is unchecked AND GST was previously saved
 */
export const removeGstThunk = createAsyncThunk<
  GstOperationResult,
  RemoveGstParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  'hotels/gst/remove',
  async (params, { rejectWithValue }) => {
    try {
      const sessionId = getOrCreateSessionId();
      const userId = getUUID();

      logger.info('[GstThunk:remove] Starting operation');

      const response = await callGstDetailsApi({
        sessionId,
        userId,
        transactionKey: params.transactionKey,
        actionType: 'hotel_gst_detail_remove',
        gstDetails: null,
      });

      if (!response.success && response.error) {
        logger.warn('[GstThunk:remove] API error:', response.error);
        return rejectWithValue(response.error.message);
      }

      logger.info('[GstThunk:remove] Success', { isGstApplied: response.isGstApplied });

      // Return null GST details and isGstApplied from backend
      return { gstDetails: null, isGstApplied: response.isGstApplied };
    } catch (error) {
      logger.error('[GstThunk:remove] Failed:', error);
      if (error instanceof GstDetailsError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to remove GST details');
    }
  },
  {
    // Prevent duplicate API calls while loading
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return !state.hotels.bookingReview.isGstOperationLoading;
    },
  },
);
